import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { StatusPill, type Tone } from '@/components/DeliveryStatusBadge'
import { formatDate } from '../utils/format'

/**
 * Pacing bands, in percentage points between delivery and the "today" tick.
 * Within ±ON_TRACK the bar is green; behind it fades amber → red, ahead it
 * fades blue.
 */
const GAP_ON_TRACK = 5
const GAP_WARN = 10
const GAP_BAD = 30
const GAP_AHEAD = 20

/** Blend two theme colours; t=0 → a, t=1 → b. */
function mix(a: string, b: string, t: number) {
  const pct = Math.round(Math.min(1, Math.max(0, t)) * 100)
  return `color-mix(in oklab, var(${b}) ${pct}%, var(${a}))`
}

/** Where a line item is in its flight, as both the bar and the card read it. */
export interface FlightState {
  start: Date | null
  end: Date | null
  /** Before the start date — nothing should have delivered yet. */
  future: boolean
  /** Past the end date. */
  elapsed: boolean
  /** Past the end date, or stopped there by the ad server. */
  ended: boolean
  /** Share of the flight that has passed, 0-100. */
  timePct: number
  /** Milliseconds to the end date, or null once it has passed. */
  msLeft: number | null
}

export function flightStateOf(
  startDate: string,
  endDate: string,
  executionState: string,
  elapsedTimePct: number | null
): FlightState {
  const start = startDate ? new Date(startDate) : null
  const end = endDate ? new Date(endDate) : null
  const now = new Date()
  const elapsed = end ? now > end : false
  const future = start ? now < start : false
  return {
    start,
    end,
    future,
    elapsed,
    ended: executionState === 'ENDED' || elapsed,
    timePct: elapsedTimePct ?? 0,
    msLeft: end && !elapsed ? end.getTime() - now.getTime() : null,
  }
}

/** How delivery compares to the flight time that has passed. */
export type PacingStatus = 'none' | 'overDelivering' | 'ahead' | 'onTrack' | 'behind' | 'farBehind'

export interface Pacing {
  status: PacingStatus
  /** Delivery minus elapsed time, in percentage points. Null when not scored. */
  gap: number | null
  /** Colour for the delivery fill, already a CSS value. */
  color: string
}

/**
 * Scores pacing while the campaign is live — a big lag behind the "today" tick
 * reads red. A campaign that has not started, has ended, or has no delivery
 * figure is not scored at all: there is nothing to be behind of. One that
 * ended fully delivered still gets the green fill.
 */
export function pacingOf(flight: FlightState, deliveryPct: number | null): Pacing {
  // An ended campaign that delivered in full is a success, not a neutral grey —
  // grey there reads as a campaign that failed.
  if (flight.ended && deliveryPct !== null && Math.round(deliveryPct) >= 100) {
    return { status: 'none', gap: null, color: 'var(--ad-pacing-good)' }
  }
  const scored = !flight.ended && !flight.future && deliveryPct !== null && flight.timePct > 0
  if (!scored) return { status: 'none', gap: null, color: 'var(--ad-pacing-none)' }

  const gap = deliveryPct! - flight.timePct
  if (gap >= GAP_AHEAD) return { status: 'overDelivering', gap, color: 'var(--ad-pacing-ahead)' }
  if (gap > GAP_ON_TRACK) {
    return {
      status: 'ahead',
      gap,
      color: mix(
        '--ad-pacing-good',
        '--ad-pacing-ahead',
        (gap - GAP_ON_TRACK) / (GAP_AHEAD - GAP_ON_TRACK)
      ),
    }
  }
  if (gap >= -GAP_ON_TRACK) return { status: 'onTrack', gap, color: 'var(--ad-pacing-good)' }
  if (gap >= -GAP_WARN) {
    return {
      status: 'behind',
      gap,
      color: mix(
        '--ad-pacing-good',
        '--ad-pacing-warn',
        (-gap - GAP_ON_TRACK) / (GAP_WARN - GAP_ON_TRACK)
      ),
    }
  }
  if (gap >= -GAP_BAD) {
    return {
      status: 'farBehind',
      gap,
      color: mix('--ad-pacing-warn', '--ad-pacing-bad', (-gap - GAP_WARN) / (GAP_BAD - GAP_WARN)),
    }
  }
  return { status: 'farBehind', gap, color: 'var(--ad-pacing-bad)' }
}

/** Translation key for a pacing status, taking a `value` of whole percent. */
export const PACING_LABEL_KEYS: Record<PacingStatus, string> = {
  none: 'pacing.none',
  overDelivering: 'pacing.overDelivering',
  ahead: 'pacing.ahead',
  onTrack: 'pacing.onTrack',
  behind: 'pacing.behind',
  farBehind: 'pacing.farBehind',
}

export interface CampaignTimelineProps {
  startDate: string
  endDate: string
  executionState: string
  deliveryPct: number | null
  elapsedTimePct: number | null
  /** `sm` is the list cell; `md` gives the bar room to be read on a card. */
  size?: 'sm' | 'md'
  className?: string
}

/**
 * Delivery against flight time as one bar: the fill is how much has been
 * delivered, the tick is where today falls between start and end. Fill short
 * of the tick is a campaign running late, and the colour says by how much.
 */
export function CampaignTimeline({
  startDate,
  endDate,
  executionState,
  deliveryPct,
  elapsedTimePct,
  size = 'sm',
  className,
}: CampaignTimelineProps) {
  const { t } = useTranslation()
  const flight = flightStateOf(startDate, endDate, executionState, elapsedTimePct)
  const pacing = pacingOf(flight, deliveryPct)

  if (!flight.start && !flight.end) {
    return <span className="text-xs text-(--ad-text-muted)">—</span>
  }

  const fmt = (value: string) =>
    value
      ? formatDate(value, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : null

  const tooltip = [
    fmt(startDate) && fmt(endDate) ? `${fmt(startDate)} → ${fmt(endDate)}` : null,
    !flight.future &&
      t('pacing.timeElapsed', { value: flight.elapsed ? 100 : Math.round(flight.timePct) }),
    deliveryPct !== null ? t('pacing.deliveredValue', { value: Math.round(deliveryPct) }) : null,
    pacing.gap !== null
      ? t(PACING_LABEL_KEYS[pacing.status], { value: Math.round(Math.abs(pacing.gap)) })
      : null,
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <div
      title={tooltip}
      className={cn('w-full cursor-default', size === 'sm' && 'max-w-45 min-w-25', className)}
    >
      <div
        className={cn(
          'relative overflow-visible rounded-full bg-(--ad-badge-gray-bg)',
          size === 'sm' ? 'h-1.5' : 'h-2.5'
        )}
      >
        {/* Delivery fill */}
        {!flight.future && (deliveryPct ?? 0) > 0 && (
          <div
            className="absolute top-0 left-0 h-full overflow-hidden rounded-full transition-[width] duration-300 ease-out"
            style={{ width: `${Math.min(deliveryPct ?? 0, 100)}%`, background: pacing.color }}
          />
        )}

        {/* Today tick — vertical line at the share of the flight that has passed */}
        {!flight.future && !flight.elapsed && (
          <div
            className={cn(
              'absolute z-2 w-0.5 -translate-x-1/2 rounded-xs bg-(--ad-pacing-none) opacity-90',
              size === 'sm' ? '-top-0.75 h-3' : '-top-1 h-4.5'
            )}
            style={{ left: `${flight.timePct}%` }}
          />
        )}
      </div>
    </div>
  )
}

/** Pacing verdicts wear the same tones as delivery statuses. */
const PACING_TONES: Record<PacingStatus, Tone> = {
  none: 'gray',
  overDelivering: 'blue',
  ahead: 'blue',
  onTrack: 'green',
  behind: 'amber',
  farBehind: 'red',
}

/**
 * The pacing verdict as a pill — "On track", "12% behind schedule". A campaign
 * outside its flight is not paced at all, so pass the flight and the pill says
 * where it is instead of scoring it.
 */
export function PacingBadge({ pacing, flight }: { pacing: Pacing; flight?: FlightState }) {
  const { t } = useTranslation()
  const key =
    pacing.status === 'none' && flight?.ended
      ? 'pacing.ended'
      : pacing.status === 'none' && flight?.future
        ? 'pacing.notStarted'
        : PACING_LABEL_KEYS[pacing.status]
  return (
    <StatusPill tone={PACING_TONES[pacing.status]}>
      {t(key, { value: Math.round(Math.abs(pacing.gap ?? 0)) })}
    </StatusPill>
  )
}
